import React from 'react';
import PropTypes from 'prop-types';
import { App } from '@meronex/app';
import IArrow from '@meronex/icons/bi/BisDownArrow';
import IBack from '@meronex/icons/fi/FiArrowLeft';
import IPlus from '@meronex/icons/fi/FiPlus';
import MdClose from '@meronex/icons/md/MdClose';

import { direction } from 'direction';

import { useTheme } from '@material-ui/core/styles';
import Filter from '../../common/Filter';

const TopBar = React.forwardRef((props, ref) => {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const {
    icon,
    title,
    titleMaxWidth,
    onClose,
    onTitleDropdownClick,
    showMenu,
    menuStyle,
    activeView,
    subtitle,
    itemsCount,
    itemsUnit,
    filterProps,
    onRefresh,
    onBack,
    actionLabel,
    onAction,
    children,
  } = props;

  const titleDirection = direction(title);

  const filterRef = React.useRef();

  React.useImperativeHandle(ref, () => ({
    reset: () => {
      if (filterRef && filterRef.current) {
        filterRef.current.reset();
      }
    },
  }));

  const renderItemUnit = () => {
    if (typeof itemsUnit === 'string') {
      return (
        <span>
          &nbsp;{itemsUnit}
          {itemsCount > 1 ? 's' : ''}
        </span>
      );
    } else if (typeof itemsUnit === 'object') {
      if (itemsCount > 1 && itemsUnit.plural) {
        return <span>&nbsp;{itemsUnit.plural}</span>;
      } else if (itemsCount <= 1 && itemsUnit.single) {
        return <span>&nbsp;{itemsUnit.single}</span>;
      }
    }
  };

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      {/* Title row */}
      <div
        className={'noselect'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minHeight: '44px',
        }}>
        {/* Left side: Back + icon + title */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1,
            minWidth: 0,
          }}
          onClick={() => {
            if (onTitleDropdownClick) {
              onTitleDropdownClick();
            }
          }}>
          {onBack && (
            <div
              onClick={(e) => { e.stopPropagation(); onBack(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                cursor: 'pointer',
                color: isDark ? '#94a3b8' : '#475569',
                flexShrink: 0,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
              <IBack size={20} />
            </div>
          )}
          {icon && (
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: isDark
                ? 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(79,70,229,0.25))'
                : 'linear-gradient(135deg, #6366f1, #4f46e5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: isDark ? '#a5b4fc' : '#fff',
            }}>
              {icon}
            </div>
          )}
          {title && (
            <div style={{ minWidth: 0, flex: 1, cursor: onTitleDropdownClick ? 'pointer' : 'default' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <span style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: 1.3,
                  color: isDark ? '#e2e8f0' : '#0f172a',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  minWidth: 0,
                }}>
                  {titleDirection === 'rtl' && Boolean(itemsCount) && (
                    <span style={{ marginRight: '5px' }}>
                      ({itemsCount})
                    </span>
                  )}
                  {title}
                </span>
                {titleDirection === 'ltr' && Boolean(itemsCount) && (
                  <span style={{
                    fontSize: '12px',
                    color: isDark ? '#64748b' : '#94a3b8',
                    fontWeight: 500,
                    flexShrink: 0,
                  }}>
                    ({itemsCount}{renderItemUnit()})
                  </span>
                )}
              </div>
              {subtitle && (
                <div style={{
                  fontSize: '12px',
                  color: '#94a3b8',
                  marginTop: '2px',
                }}>
                  {subtitle}
                </div>
              )}
            </div>
          )}
          {onTitleDropdownClick && (
            <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: isDark ? '#64748b' : '#94a3b8' }}>
              <IArrow size={10} />
            </span>
          )}
        </div>

        {/* Right side: Action button */}
        {onAction && actionLabel && (
          <div
            onClick={onAction}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: '#4f46e5',
              color: '#fff',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              flexShrink: 0,
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#4338ca'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#4f46e5'; }}>
            <IPlus size={16} />
            <span>{actionLabel}</span>
          </div>
        )}

        {/* Close button — inline in flex row */}
        {onClose && (
          <div
            onClick={() => { onClose(); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: isDark ? '#94a3b8' : '#64748b',
              flexShrink: 0,
              fontSize: '20px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <MdClose />
          </div>
        )}
      </div>

      {/* Filter */}
      {filterProps && (
        <div style={{ marginTop: '8px' }}>
          <Filter ref={filterRef} {...filterProps} />
        </div>
      )}

      {/* Extra children */}
      {children}


    </div>
  );
});
TopBar.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  titleMaxWidth: PropTypes.number,
  showMenu: PropTypes.bool,
  filterProps: PropTypes.shape(Filter.propTypes),
  menuStyle: PropTypes.object,
  activeView: PropTypes.string,
  subtitle: PropTypes.string,
  itemsCount: PropTypes.number,
  itemsUnit: PropTypes.string,
  onTitleDropdownClick: PropTypes.func,
  onRefresh: PropTypes.func,
  onClose: PropTypes.func,
  onBack: PropTypes.func,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
};

TopBar.defaultProps = {
  titleMaxWidth: 280,
};
export default TopBar;
